import { faBan } from '@fortawesome/free-solid-svg-icons';

import Button from '@/components/Button';
import Stack from '@/components/Stack';
import { Body, Subheading } from '@/components/Typography';
import Icon from '@/elements/Icon';
import Text from '@/elements/Text';
import styled from '@/styles';
import { WorkerDetail } from '@/services/workersV2API';
import {
  getAccountStatusLabel,
  getSuspensionStatusLabel,
} from '@/util/workerStatus';

type Props = {
  worker: Pick<
    WorkerDetail,
    | 'is_suspended'
    | 'suspension_type'
    | 'suspension_ends_at'
    | 'suspension_worker_visible_reason'
    | 'active_suspension'
    | 'active'
  >;
  onLiftSuspension?: () => void;
  onReactivate?: () => void;
  liftingSuspension?: boolean;
  reactivating?: boolean;
};

const Wrapper = styled('div', {
  alignItems: 'flex-start',
  display: 'flex',
  gap: '1rem',
  padding: '1rem',
  backgroundColor: '$dangerA8',
  border: '1px solid $dangerLightest',
  borderRadius: '$lg',
  color: '#CF040C',
  width: '100%',
});

const formatSuspensionDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const WorkerSuspensionBanner = ({
  worker,
  onLiftSuspension,
  onReactivate,
  liftingSuspension = false,
  reactivating = false,
}: Props) => {
  if (!worker.is_suspended && worker.active) return null;

  const isSuspended = worker.is_suspended;
  const isPermanent = worker.suspension_type === 'permanent';
  const isWorkerRequest =
    worker.active_suspension?.worker_visible_reason === 'worker_request';
  const accountStatusLabel = getAccountStatusLabel(worker);
  const suspensionStatusLabel = getSuspensionStatusLabel(worker);

  let title: string;
  let description: React.ReactNode;

  if (isSuspended && !isPermanent) {
    title = worker.active
      ? 'Worker Is Temporarily Suspended'
      : 'Worker Is Suspended and Inactive';
    const reason = worker.suspension_worker_visible_reason;
    const endDate = formatSuspensionDate(worker.suspension_ends_at);
    description = (
      <Stack vertical gap={4} align="start">
        <Body>
          Suspension status: <strong>{suspensionStatusLabel}</strong>
          {reason && (
            <>
              {' '}
              due to <strong>{reason}</strong>
            </>
          )}
          {endDate && (
            <>
              . Suspension ends on <strong>{endDate}</strong>
            </>
          )}
          .
        </Body>
        {!worker.active && (
          <Body>
            Account status: <strong>{accountStatusLabel}</strong>. Lifting the
            suspension will not reactivate the account.
          </Body>
        )}
      </Stack>
    );
  } else if (isSuspended && isPermanent && !isWorkerRequest) {
    title = worker.active
      ? 'Worker Is Permanently Suspended'
      : 'Worker Is Permanently Suspended and Inactive';
    const reason = worker.suspension_worker_visible_reason;
    description = reason ? (
      <Stack vertical gap={4} align="start">
        <Body>
          Suspension status: <strong>{suspensionStatusLabel}</strong> due to{' '}
          <strong>{reason}</strong>.
        </Body>
        {!worker.active && (
          <Body>
            Account status: <strong>{accountStatusLabel}</strong>.
          </Body>
        )}
      </Stack>
    ) : (
      <Stack vertical gap={4} align="start">
        <Body>
          Suspension status: <strong>{suspensionStatusLabel}</strong> due to a
          violation.
        </Body>
        {!worker.active && (
          <Body>
            Account status: <strong>{accountStatusLabel}</strong>.
          </Body>
        )}
      </Stack>
    );
  } else if (isSuspended && isPermanent && isWorkerRequest) {
    title = worker.active
      ? 'Worker Requested Deactivation'
      : 'Worker Requested Deactivation and Is Inactive';
    description = (
      <Stack vertical gap={4} align="start">
        <Body>
          Suspension status: <strong>{suspensionStatusLabel}</strong>. This
          account was disabled at the worker&apos;s request.
        </Body>
        {!worker.active && (
          <Body>
            Account status: <strong>{accountStatusLabel}</strong>.
          </Body>
        )}
      </Stack>
    );
  } else {
    title = 'Worker Is Inactive';
    description = (
      <Stack vertical gap={4} align="start">
        <Body>
          Account status: <strong>{accountStatusLabel}</strong>. Reactivate the
          worker to restore platform access.
        </Body>
        <Body>
          Suspension status: <strong>{suspensionStatusLabel}</strong>.
        </Body>
      </Stack>
    );
  }

  return (
    <Wrapper>
      <Text color="inherit" size="lg">
        <Icon fixedWidth icon={faBan} />
      </Text>
      <Stack vertical align="start" gap={4}>
        <Subheading>
          <strong>{title}</strong>
        </Subheading>
        {description}
        {worker.is_suspended &&
          worker.active_suspension &&
          onLiftSuspension && (
            <div style={{ marginTop: '8px' }}>
              <Button
                a11yLabel="Lift worker suspension"
                appearance="outline"
                isLoading={liftingSuspension}
                label="Lift Suspension"
                status="success"
                onClick={onLiftSuspension}
              />
            </div>
          )}
        {worker.is_suspended &&
          worker.active_suspension &&
          !onLiftSuspension && (
            <Body>You do not have permission to lift this suspension.</Body>
          )}
        {!worker.active && onReactivate && (
          <div style={{ marginTop: '8px' }}>
            <Button
              a11yLabel="Reactivate worker"
              appearance="outline"
              isLoading={reactivating}
              label="Reactivate Worker"
              status="success"
              onClick={onReactivate}
            />
          </div>
        )}
      </Stack>
    </Wrapper>
  );
};

export default WorkerSuspensionBanner;
